import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import Dropdown from "app/components/Dropdown";
import { CheckIcon } from "app/components/Icons";
import TicketTypeIcon from "app/components/TicketTypeIcon";

import { ITicketTypes } from "app/containers/Features/containers/TeamSettings/container/Projects/container/WorkItems/types";
import { selectTicketTypes } from "app/redux/selector";
import { selectDraftBoardForm } from "../containers/Boards/components/Settings/redux/selector";
import { filterTicketTypes } from "../containers/TeamSettings/container/Projects/container/WorkItems/helper";

interface CustomProps {
  value?: string;
  setValue?: (value: ITicketTypes) => void;
  trigger: React.ReactNode;
  align?: "start" | "end" | "center";
  isHeader?: string;
  className?: string;
  isHeaderClassName?: string;
  showEndIcon?: boolean;
  showDefault?: boolean;
  setOpen?: () => void;
  dropDownClassName?: string;
  isUnPaddedY?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  showFilteredTypes?: boolean;
}

const CommonTicketTypeSelection: React.FC<CustomProps> = ({
  setValue,
  value,
  trigger,
  align = "center",
  isHeader,
  className,
  isHeaderClassName,
  showEndIcon,
  showDefault,
  setOpen,
  dropDownClassName,
  isUnPaddedY = false,
  onOpen,
  onClose,
  showFilteredTypes,
}) => {
  const ticketTypes = useSelector(selectTicketTypes);
  const boardData = useSelector(selectDraftBoardForm);

  // ✅ Memoized filtering
  const filteredTypes = useMemo(() => {
    if (!ticketTypes) return [];
    return showFilteredTypes
      ? filterTicketTypes(ticketTypes)
      : ticketTypes.filter((x) => !x.isArchived);
  }, [ticketTypes, showFilteredTypes]);

  // ✅ Memoized menu items
  const menuItems = useMemo(
    () =>
      filteredTypes.map((ele) => {
        const isSelected = ele._id === value;
        const isDefault = ele._id === boardData?.defaultTicketType;

        return {
          label: (
            <div className="flex items-center justify-between w-full gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="min-w-[20px]">
                  <TicketTypeIcon iconName={ele.icon} />
                </div>

                <div
                  className={`text-sm leading-[18px] truncate ${
                    isDefault ? "font-medium" : "font-normal"
                  } text-secondary`}
                >
                  {ele.name}

                  {showDefault && isDefault && (
                    <span className="ps-2 text-[#BDC0C3] text-xs">
                      (Default)
                    </span>
                  )}
                </div>
              </div>

              {showEndIcon && isSelected && (
                <CheckIcon color="#5A8DF1" />
              )}
            </div>
          ),
          action: () => setValue?.(ele),
          isUnPaddedY,
        };
      }),
    [filteredTypes, value, boardData, showDefault, showEndIcon, setValue, isUnPaddedY]
  );

  return (
    <Dropdown
      setOpen={setOpen}
      trigger={trigger}
      projectInfoIcon={
        isHeader ? (
          <div
            className={`${isHeaderClassName} flex items-center justify-between px-4 py-2 text-secondary tracking-[0.56px] text-sm font-semibold leading-[21px] uppercase`}
          >
            <span>{isHeader}</span>
          </div>
        ) : null
      }
      menuItems={menuItems}
      align={align}
      className={`${className} py-1`}
      dropDownClassName={dropDownClassName}
      onOpen={onOpen}
      onClose={onClose}
    />
  );
};

export default CommonTicketTypeSelection;